import { ChevronDown, ChevronRight, FolderOpen, RotateCcw, Search } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

export type CascadeFolder = { objectId: string; title: string; parentId?: string };

type CascadeFolderPickerProps = {
  label: string;
  value: string;
  folders: CascadeFolder[];
  loading?: boolean;
  onChange: (value: string) => void;
  actions?: ReactNode;
  footer?: ReactNode;
  emptyLabel?: string;
};

export function CascadeFolderPicker({ label, value, folders, loading = false, onChange, actions, footer, emptyLabel = 'No folder selected' }: CascadeFolderPickerProps) {
  const [open, setOpen] = useState(false);
  const [parentId, setParentId] = useState('');
  const [search, setSearch] = useState('');
  const folderMap = useMemo(() => new Map(folders.map((folder) => [folder.objectId, folder])), [folders]);
  const currentFolder = value ? folderMap.get(value) : undefined;
  const visibleFolders = folders.filter((folder) => search.trim()
    ? folder.title.toLowerCase().includes(search.trim().toLowerCase())
    : (folder.parentId || '') === parentId);

  useEffect(() => {
    if (!loading && value && !folderMap.has(value)) onChange('');
  }, [folderMap, loading, onChange, value]);

  const selectFolder = (folderId: string) => {
    onChange(folderId);
    setOpen(false);
  };

  const breadcrumb: CascadeFolder[] = [];
  let breadcrumbFolder = parentId ? folderMap.get(parentId) : undefined;
  while (breadcrumbFolder) {
    breadcrumb.unshift(breadcrumbFolder);
    breadcrumbFolder = breadcrumbFolder.parentId ? folderMap.get(breadcrumbFolder.parentId) : undefined;
  }

  return <div className="test-cascade-picker">
    <div className="test-cascade-label"><span>{label}</span>{actions}</div>
    <button className={`test-cascade-trigger${open ? ' is-open' : ''}`} type="button" onClick={() => setOpen((current) => !current)} aria-expanded={open}>
      <span className="test-cascade-trigger-copy"><FolderOpen size={16} /><strong>{loading ? 'Loading folders…' : currentFolder?.title || emptyLabel}</strong></span>
      <ChevronDown size={16} />
    </button>
    {open && <div className="test-cascade-panel">
      <div className="test-search-field test-cascade-search"><Search size={15} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search folders" aria-label="Search folders" /></div>
      <div className="test-cascade-breadcrumb">
        <button type="button" className={!parentId ? 'active' : ''} onClick={() => { setParentId(''); setSearch(''); }}>All folders</button>
        {breadcrumb.map((folder) => <span key={folder.objectId}><ChevronRight size={13} /><button type="button" className={folder.objectId === parentId ? 'active' : ''} onClick={() => setParentId(folder.objectId)}>{folder.title}</button></span>)}
      </div>
      <div className="test-cascade-list">
        <button type="button" className={`test-cascade-option${!value ? ' selected' : ''}`} onClick={() => selectFolder('')}><RotateCcw size={15} /><span>{emptyLabel}</span></button>
        {visibleFolders.map((folder) => {
          const hasChildren = folders.some((child) => child.parentId === folder.objectId);
          return <div className={`test-cascade-option${folder.objectId === value ? ' selected' : ''}`} key={folder.objectId}>
            <button type="button" className="test-cascade-select" onClick={() => selectFolder(folder.objectId)}><FolderOpen size={15} /><span>{folder.title}</span></button>
            {hasChildren && <button type="button" className="test-cascade-next" onClick={() => setParentId(folder.objectId)} aria-label={`Open ${folder.title}`} title={`Open ${folder.title}`}><ChevronRight size={16} /></button>}
          </div>;
        })}
        {!visibleFolders.length && <span className="test-cascade-empty">{loading ? 'Loading folders…' : search.trim() ? 'No folders match this search.' : 'No folders in this level.'}</span>}
      </div>
    </div>}
    {footer && <div className="test-cascade-creation-area"><p className="test-cascade-creation-hint">{currentFolder ? <>Creating this folder under <strong>{currentFolder.title}</strong>.</> : 'Creating this folder at the root level.'}</p>{footer}</div>}
  </div>;
}
